import { useEffect, useState, useCallback } from "react";
import { SlidersHorizontal } from "lucide-react";
import { CarCard } from "../components/CarCard";
import { MaskLine } from "../components/Motion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api } from "../lib/api";

const ALL = "__all__";

export default function Occasions() {
  const [cars, setCars] = useState([]);
  const [facets, setFacets] = useState({ merken: [], brandstoffen: [], transmissies: [] });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ merk: "", brandstof: "", transmissie: "", sort: "nieuwste" });

  useEffect(() => {
    api.get("/cars/facets").then((r) => setFacets(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filters.merk) params.merk = filters.merk;
    if (filters.brandstof) params.brandstof = filters.brandstof;
    if (filters.transmissie) params.transmissie = filters.transmissie;
    params.sort = filters.sort;
    api.get("/cars", { params }).then((r) => setCars(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v === ALL ? "" : v }));

  return (
    <div className="pt-32 pb-24 min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <p className="text-xs font-bold tracking-[0.25em] uppercase text-primary mb-4">Ons aanbod</p>
        <h1 className="font-display font-black uppercase tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-none text-text-main">
          <MaskLine>Occasions</MaskLine>
        </h1>

        {/* Filters */}
        <div className="mt-12 border-y border-border py-5" data-testid="filter-bar">
          <div className="flex items-center gap-2 text-sm font-semibold text-text-main mb-4 sm:hidden">
            <SlidersHorizontal size={16} /> Filter &amp; sorteer
          </div>
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center gap-3 sm:gap-4">
            <div className="hidden sm:flex items-center gap-2 text-sm font-semibold text-text-main mr-2">
              <SlidersHorizontal size={16} /> Filter
            </div>
            <FilterSelect testid="filter-merk" placeholder="Merk" value={filters.merk} onChange={set("merk")} options={facets.merken} />
            <FilterSelect testid="filter-brandstof" placeholder="Brandstof" value={filters.brandstof} onChange={set("brandstof")} options={facets.brandstoffen} />
            <FilterSelect testid="filter-transmissie" placeholder="Transmissie" value={filters.transmissie} onChange={set("transmissie")} options={facets.transmissies} />
            <div className="sm:ml-auto">
              <Select value={filters.sort} onValueChange={set("sort")}>
                <SelectTrigger data-testid="filter-sort" className="w-full sm:w-[180px] rounded-full border-border bg-surface">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nieuwste">Nieuwste eerst</SelectItem>
                  <SelectItem value="prijs_op">Prijs oplopend</SelectItem>
                  <SelectItem value="prijs_af">Prijs aflopend</SelectItem>
                  <SelectItem value="km">Kilometerstand</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <p className="mt-6 text-sm text-text-muted" data-testid="results-count">
          {loading ? "Laden..." : `${cars.length} occasions gevonden`}
        </p>

        {!loading && cars.length === 0 && (
          <div className="py-24 text-center text-text-muted" data-testid="no-results">
            Geen auto's gevonden met deze filters.
          </div>
        )}

        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="occasions-grid">
          {cars.map((car, i) => <CarCard key={car.id} car={car} index={i} />)}
        </div>
      </div>
    </div>
  );
}

const FilterSelect = ({ placeholder, value, onChange, options, testid }) => (
  <Select value={value || ALL} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="w-full sm:w-[150px] rounded-full border-border bg-surface">
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value={ALL}>Alle {placeholder.toLowerCase()}</SelectItem>
      {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
    </SelectContent>
  </Select>
);
